import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ICONS, PATHS, IntroContent } from 'src/common/constants';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import {
  setAssetFormData,
  clearAssetFormData,
} from 'src/store/actions/assetForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Loader from '../Loader';
import RoomAccordionSkeleton from 'src/components/AssetsTable/skeletons/RoomAccordionSkeleton';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import AreaDocument from './AreaDocument';
import { variables } from 'src/Tenants';
import ContentBlock from '../ContentBlock';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import AddRoomDialog from 'src/components/AssetsTable/AreaAssetTableComponents/AddRoomDialog';
import AddAssetDialog from 'src/components/AddAssetDialogue/AddAssetDialogue';
import Search from 'src/components/Search/Search';
import RoomAccordionList from 'src/components/RoomAccordionList/RoomAccordionList';
import './AreaDocument.scss';
import './AreaAssetsTable.scss';

interface RoomsAndAssetsProps {
  assets: any[];
  room: any;
  viewAsset: string;
  viewAssetId: string;
  user: any;
  onDeleteAsset: (areaId: string, asset: any) => void;
  history: any;
  suite: any;
  property: any;
  dispatchSetAssetFormData: (data: any) => void;
  setShowOverlay: (show: boolean) => void;
  setNewAsset: (isNew: boolean) => void;
}

const RoomsAndAssets = ({
  assets,
  room,
  viewAsset,
  viewAssetId,
  user,
  onDeleteAsset,
  history,
  suite,
  property,
  dispatchSetAssetFormData,
  setShowOverlay,
  setNewAsset,
}: RoomsAndAssetsProps) => {

  const processedRooms: any[] = useMemo(() => {
    const assetsWithItem: any[] = assets.filter((a: any) => a.item !== null);

    if (assetsWithItem.length === 0) {
      return assets;
    }

    const assetEntriesByItemName: [string, any][] = assetsWithItem.map(
      (asset: any) => [asset.item.name, asset]
    );

    const assetsKeyedByItemName: Map<string, any> = new Map(assetEntriesByItemName);

    const uniqueAssetsByItemName: any[] = Array.from(assetsKeyedByItemName.values());

    return [...uniqueAssetsByItemName].sort((a: any, b: any) => {
      const nameA: string = a.item?.name?.toLowerCase() ?? '';
      const nameB: string = b.item?.name?.toLowerCase() ?? '';
      return nameA.localeCompare(nameB);
    });
  }, [assets]);

  if (viewAsset !== room.name) {
    return null;
  }

  return (
    <>
      {processedRooms.map((asset: any, x: number) => {
        if (asset.item === null) {
          return null;
        }

        return (
          <div key={`${asset.id}-${x}`} className="areadocuments__body">
            <AreaDocument
              user={user}
              onDelete={() => onDeleteAsset(viewAssetId, asset)}
              headline={asset.item && asset.item.name ? asset.item.name : ''}
              onView={() => {
                history.push({
                  pathname: PATHS.ASSETS_VIEW.replace(
                    ':id',
                    viewAssetId + '&' + asset.id
                  ),
                  state: {
                    asset: asset,
                    suite: suite,
                    property: property,
                  },
                });
              }}
              onEdit={() => {
                dispatchSetAssetFormData({
                  ...asset,
                  areaId: viewAssetId,
                  assetWarrantyDefaultStartDate:
                    suite.assetWarrantyDefaultStartDate,
                });
                setShowOverlay(true);
                setNewAsset(false);
              }}
              icon={
                <FontAwesomeIcon
                  glyph={
                    ICONS[
                      asset.item &&
                      asset.item.itemTypeIcons &&
                      asset.item.itemTypeIcons.name
                        ? asset.item.itemTypeIcons.name
                        : ''
                    ]
                  }
                  weight="light"
                />
              }
              active={undefined}
              strapline={undefined}
              itemTypeWalkthrough={undefined}
              small={undefined}
              onImageClicked={undefined}
            />
          </div>
        );
      })}
    </>
  );
};

interface AreaAssetsTableProps {
  assets: any[];
  loader?: boolean;
  loading?: boolean;
  onSaveAsset: (asset: any) => void;
  onDeleteAsset: (areaId: string, assetId: string) => void;
  onDeleteRoom: (unitAreaId: string) => Promise<boolean>;
  deleteRoomLoading: boolean;
  onClearDeleteRoomError: () => void;
  onSearch: (value: string) => void;
  suite: any;
  property: any;
  history: any;
  introContent?: boolean;
}

const AreaAssetsTable = ({
  assets,
  loader: propLoader,
  loading = false,
  onSaveAsset,
  onDeleteAsset,
  onDeleteRoom,
  deleteRoomLoading,
  onClearDeleteRoomError,
  onSearch,
  suite,
  property,
  history,
  introContent,
}: AreaAssetsTableProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [viewAsset, setViewAsset] = useState<string>('');
  const [viewAssetId, setViewAssetId] = useState<string>('');
  const [newAsset, setNewAsset] = useState<boolean>(false);
  const [prevAssetsLength, setPrevAssetsLength] = useState<number>(0);
  const [roomToDelete, setRoomToDelete] = useState<any>(null);
  const [assetToDelete, setAssetToDelete] = useState<{
    areaId: string;
    asset: any;
  } | null>(null);
  const [showAddRoomDialog, setShowAddRoomDialog] = useState<boolean>(false);

  const search: any = useSelector(selectSearch);
  const user: any = useSelector(selectAuthenticatedUser);
  const loader: any = useSelector(selectFormLoader);

  const dispatch = useDispatch();
  const dispatchSetAssetFormData = (data: any) =>
    dispatch(setAssetFormData(data));
  const dispatchClearAssetFormData = () => dispatch(clearAssetFormData());

  useEffect(() => {
    openFirstRoom();
  }, []);

  useEffect(() => {
    setPrevAssetsLength(assets.length);
  }, [assets.length]);

  useEffect(() => {
    if (assets && assets.length > 0 && assets.length > prevAssetsLength) {
      setViewAsset(viewAsset === assets[0].name ? '' : assets[0].name);
      setViewAssetId(viewAssetId === assets[0].areaId ? '' : assets[0].areaId);
    }
  }, [assets, prevAssetsLength, viewAsset, viewAssetId]);

  const sortedAssets = useMemo(() => {
    return [...assets].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();

      return nameA.localeCompare(nameB);
    });
  }, [assets]);

  const openFirstRoom = () => {
    if (assets && assets.length > 0) {
      setViewAsset(assets[0].name);
      setViewAssetId(assets[0].areaId);
    }
  };

  const expandRoom = (room: any) => {
    setViewAsset(viewAsset === room.name ? '' : room.name);
    setViewAssetId(viewAssetId === room.areaId ? '' : room.areaId);
  };

  const handleDeleteRoom = async () => {
    if (!roomToDelete) return;

    const success = await onDeleteRoom(roomToDelete.id);

    if (success) {
      if (viewAsset === roomToDelete.name) {
        setViewAsset('');
        setViewAssetId('');
      }
      setRoomToDelete(null);
    }
  };

  const handleConfirmDeleteAsset = () => {
    if (assetToDelete) {
      onDeleteAsset(assetToDelete.areaId, assetToDelete.asset.id);
      setAssetToDelete(null);
    }
  };

  const handleCancelDeleteRoom = () => {
    setRoomToDelete(null);
    onClearDeleteRoomError();
  };

  const homeOwnerCheck: boolean =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === 'Home Owner' ||
      user.roles[0].role.name === 'Occupant' ||
      user.roles[0].role.name === 'Strata Manager' ||
      user.roles[0].role.name === 'Realtor');

  const handleAddAsset = () => {
    dispatchSetAssetFormData({
      assetWarrantyDefaultStartDate:
        suite && suite.assetWarrantyDefaultStartDate
          ? suite.assetWarrantyDefaultStartDate
          : null,
      dateOfPurchase:
        suite && suite.assetWarrantyDefaultStartDate
          ? suite.assetWarrantyDefaultStartDate
          : null,
    });
    setShowOverlay(true);
    setNewAsset(true);
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    dispatchClearAssetFormData();
  };

  return (
    <>
      {loader ? <Loader center={true} /> : null}
      {introContent && <ContentBlock content={IntroContent.rooms} />}
      <div className="area-assets-header">
        <div className="area-assets-search">
          <Search
            size="small"
            placeholder="Search Assets..."
            onChange={onSearch}
            theme={undefined}
          />
        </div>
        {homeOwnerCheck ? null : (
          <div className="area-assets-header-actions">
            <Button
              color="semantic"
              label="Add Room"
              onClick={() => setShowAddRoomDialog(true)}
              icon={
                <FontAwesomeIcon
                  glyph="door-open"
                  weight="light"
                  size="small"
                />
              }
              size="small"
              style={{ color: '#000', fontWeight: '600', marginRight: '1.25rem', marginLeft: '1rem' }}
            />
            <Button
              color="teal"
              label="Add Asset"
              onClick={handleAddAsset}
              icon={
                <FontAwesomeIcon glyph="boxes" weight="light" size="small" />
              }
              size="small"
            />
          </div>
        )}
      </div>
      {loading ? (
        <RoomAccordionSkeleton />
      ) : sortedAssets.length > 0 ? (
        <div style={{ paddingTop: 15, marginLeft: 10, marginRight: 10 }}>
          <RoomAccordionList
            rooms={sortedAssets}
            expandedRoom={viewAsset}
            onToggleRoom={expandRoom}
            onDeleteRoom={!homeOwnerCheck ? setRoomToDelete : undefined}
            renderRoomContent={(room) => (
              <RoomsAndAssets
                assets={room.assets}
                room={room}
                viewAsset={viewAsset}
                viewAssetId={viewAssetId}
                user={user}
                onDeleteAsset={(areaId, asset) =>
                  setAssetToDelete({ areaId, asset })
                }
                history={history}
                suite={suite}
                property={property}
                dispatchSetAssetFormData={dispatchSetAssetFormData}
                setShowOverlay={setShowOverlay}
                setNewAsset={setNewAsset}
              />
            )}
          />
        </div>
      ) : (
        <Empty
          headline={'This ' + variables.suite + ' is empty'}
          strapline={
            'Add assets to this ' +
            variables.suite +
            ' and attach relevant documentation such as manuals and warranties.'
          }
          searchIsActive={search && search.assets ? search.assets : ''}
          cta={
            homeOwnerCheck ? null : (
              <Button
                color="teal"
                label="Add Asset"
                onClick={handleAddAsset}
                icon={
                  <FontAwesomeIcon glyph="boxes" weight="light" size="small" />
                }
                size="small"
              />
            )
          }
          image={
            <img
              src="/assets/empty/assets.svg"
              alt={`This ${variables.suite} is empty`}
            />
          }
          direction={undefined}
        />
      )}
      <AddRoomDialog
        isOpen={showAddRoomDialog}
        onClose={() => setShowAddRoomDialog(false)}
        suiteId={suite.id}
        userId={user.id}
        roleName={user.roles?.[0]?.role?.name || ''}
      />
      <AddAssetDialog
        isOpen={showOverlay}
        onClose={handleCloseOverlay}
        onSaveAsset={onSaveAsset}
        newAsset={newAsset}
        propertyId={suite.propertyId}
      />
      {assetToDelete && (
        <ConfirmationModal
          title={`Remove ${assetToDelete.asset.item.name}`}
          message={
            <>
              Are you sure you want to delete{' '}
              <strong>{assetToDelete.asset.item.name}</strong> from{' '}
              <strong>{viewAsset}</strong>?
            </>
          }
          warningMessage="This does not delete this Asset. However it will remove associated manuals, warranties, and maintenance reminders from this suite."
          confirmLabel="Remove Asset From Suite"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={handleConfirmDeleteAsset}
          onCancel={() => setAssetToDelete(null)}
        />
      )}
      {roomToDelete && (
        <ConfirmationModal
          title={`Delete ${roomToDelete.customLabel || roomToDelete.name}?`}
          message={
            <>
              This will permanently remove{' '}
              <strong>{roomToDelete.customLabel || roomToDelete.name}</strong>{' '}
              from this {variables.suite}. This action cannot be undone.
            </>
          }
          warningMessage={
            <>
              Assets within this room will not be deleted — they will remain in
              the system but will no longer be associated with this room.
            </>
          }
          confirmLabel="Delete Room"
          cancelLabel="Cancel"
          variant="danger"
          requireTypeToConfirm={true}
          confirmationWord="DELETE"
          isSubmitting={deleteRoomLoading}
          onConfirm={handleDeleteRoom}
          onCancel={handleCancelDeleteRoom}
        />
      )}
    </>
  );
};

export default AreaAssetsTable;
